import React, { FC } from 'react';
import { Trans } from 'react-i18next';
import { Text } from '@components/Text/Text.component';
import { Switch } from '@form/components/Switch/Switch.component';
import { PlcAnchor } from '@layout/components/PlcAnchor/PlcAnchor.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { buildWeatherLink } from '@meteo/business/meteoUrl.utils';
import { City } from '@meteo/types';
import { SettingsTabEnum } from '@settings/business/Settings.type';
import { getPreferencesLinkProps, urlEnum } from '@utils/url';

import './NotificationsSelection.scss';

interface Props {
  favorites: City[];
  isPro: boolean;
  pushNotificationEnabled: boolean;
  pushNotificationDenied: boolean;
  emailNotificationEnabled: boolean;
  onChange?: (pushNotification: boolean, email: boolean) => void;
  error?: string;
  disabled?: boolean;
}

function getNoFavorites() {
  const meteoPreferencesLinkProps = getPreferencesLinkProps(SettingsTabEnum.forecast);
  return (
    <Trans i18nKey="notifications.pro-no-favorites" ns="notifications">
      aller dans <PlcAnchor linkProps={meteoPreferencesLinkProps}>preférences</PlcAnchor>
    </Trans>
  );
}

function getDescriptionNotifications() {
  return (
    <Trans i18nKey="notifications.description-notifications" ns="notifications">
      Basées <PlcAnchor linkProps={buildWeatherLink(urlEnum.hourByHour)}>heure par heure</PlcAnchor>
    </Trans>
  );
}

const NotificationsSelection: FC<Props> = ({
  favorites,
  isPro,
  pushNotificationEnabled,
  pushNotificationDenied,
  emailNotificationEnabled,
  onChange,
  error,
  disabled,
}) => {
  const { t } = useTranslation(['notifications']);
  const onNotificationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) onChange(event.target.checked, emailNotificationEnabled);
  };
  const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) onChange(pushNotificationEnabled, event.target.checked);
  };
  const proWithNoFavorites = isPro && favorites.length === 0;

  return (
    <div className="notifications-selection">
      <Text i18nKey="notifications.forecast.title" tag="h2" variant="h3" />
      <Text className="notifications-selection-types" tag="div" variant="p">
        <span>{t('notifications.types-selection-label')}</span>
        <div className="notifications-selection-types-switches">
          <label htmlFor="notification-selection-push">
            <Text i18nKey="notifications.push-label" tag="span" variant="small" />
            <Switch
              checked={pushNotificationEnabled}
              disabled={disabled}
              id="notification-selection-push"
              onChange={onNotificationChange}
            />
          </label>
          <label htmlFor="notification-selection-email">
            <Text i18nKey="notifications.email-label" tag="span" variant="small" />
            <Switch
              checked={emailNotificationEnabled}
              disabled={disabled}
              id="notification-selection-email"
              onChange={onEmailChange}
            />
          </label>
        </div>
      </Text>
      {pushNotificationEnabled && pushNotificationDenied && (
        <Text flavour="red" i18nKey="notifications.push-notification-disabled" variant="small" />
      )}
      {error && <Text flavour="red" i18nKey="common.error.generic" variant="small" />}
      {proWithNoFavorites && (
        <Text flavour="red" variant="small">
          {getNoFavorites()}
        </Text>
      )}
      <Text i18nKey="notifications.description" variant="p" />
      <Text variant="small">{getDescriptionNotifications()}</Text>
      <Text i18nKey="notifications.description-alerts" variant="small" />
    </div>
  );
};

export default NotificationsSelection;
